import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Card,
  Input,
  Spin,
  Tag,
  Typography,
  Row,
  Col,
  message,
  Modal,
} from "antd";
import { SearchOutlined, InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { NavLink } from "react-router-dom";
import axios from "axios";
import TransactionForm from "./transactionForm";

const { Title } = Typography;
const { Search } = Input;

const TransactionList = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [formData, setFormData] = useState({}); // For edit data

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await axios.get("http://93.127.203.113:5005/transactions");
      setTransactions(response.data);
      setFilteredTransactions(response.data);
    } catch (error) {
      message.error("Failed to fetch transactions.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    const lowerSearch = value.toLowerCase();
    const filtered = transactions.filter((record) => {
      const fieldsToSearch = [
        record.owner?.name || "",
        record.vehicle?.plateNumber || "",
        record.customer_agreement?.passenger?.firstName || "",
        record.customer_agreement?.passenger?.lastName || "",
        record.pricePerDay?.toString() || "",
        record.startDate && new Date(record.startDate).toLocaleDateString(),
        record.endDate && new Date(record.endDate).toLocaleDateString(),
      ];
      return fieldsToSearch.some((field) =>
        field.toLowerCase().includes(lowerSearch)
      );
    });
    setFilteredTransactions(filtered);
  };

  const highlightText = (text, searchText) => {
    if (!searchText) return text;
    const regex = new RegExp(`(${searchText})`, "gi");
    const parts = text.toString().split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          style={{
            backgroundColor: "#fffb8f",
            fontWeight: "bold",
          }}
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const handleAddTransaction = () => {
    setFormData({});
    setIsModalVisible(true);
  };

  const handleEditTransaction = (record) => {
    setFormData(record);
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setFormData({});
  };

  const columns = [
    {
      title: "Owner Name",
      dataIndex: ["owner", "name"],
      key: "ownerName",
      render: (text) => highlightText(text || "N/A", searchText),
    },
    {
      title: "Vehicle",
      dataIndex: ["vehicle", "plateNumber"],
      key: "vehicle",
      render: (text) => highlightText(text || "N/A", searchText),
    },
    {
      title: "Passenger",
      key: "passengerName",
      render: (_, record) =>
        highlightText(
          `${record.customer_agreement?.passenger?.firstName || ""} ${
            record.customer_agreement?.passenger?.lastName || ""
          }` || "N/A",
          searchText
        ),
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      render: (date) =>
        highlightText(
          date ? new Date(date).toLocaleDateString() : "N/A",
          searchText
        ),
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
      render: (date) =>
        highlightText(
          date ? new Date(date).toLocaleDateString() : "N/A",
          searchText
        ),
    },
    {
      title: "Price Per Day",
      dataIndex: "pricePerDay",
      key: "pricePerDay",
      render: (text) => highlightText(`$${text}`, searchText),
    },
    {
      title: "Working Days",
      dataIndex: "workingDays",
      key: "workingDays",
      render: (text) => highlightText(text || "0", searchText),
    },
    {
      title: "Net Collected",
      dataIndex: "netCollected",
      key: "netCollected",
      render: (text) => highlightText(`$${text}`, searchText),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <>
          <Button
            type="link"
            style={{ color: "#0c8c49" }}
            onClick={() => handleEditTransaction(record)}
          >
            Edit
          </Button>
          <Button type="link" style={{ color: "#0c8c49" }}>
            <NavLink to={`/transactionDetails/${record.transactionId}`}>
              <InfoCircleOutlined /> Details
            </NavLink>
          </Button>
        </>
      ),
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <Card
        style={{
          borderRadius: "10px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          borderTop: "5px solid #0c8c49",
        }}
      >
        <Title level={4} style={{ color: "#0c8c49", marginBottom: "20px" }}>
          Transactions
        </Title>
        <Row gutter={[16, 16]} style={{ marginBottom: "20px" }}>
          <Col span={18}>
            <Search
              placeholder="Search Transactions"
              allowClear
              enterButton={<SearchOutlined />}
              onSearch={handleSearch}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ width: "60%" }}
            />
          </Col>
          <Col span={6} style={{ textAlign: "right" }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddTransaction}
              style={{
                backgroundColor: "#0c8c49",
                borderColor: "#0c8c49",
              }}
            >
              Add Transaction
            </Button>
          </Col>
        </Row>
        {loading ? (
          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={filteredTransactions}
            rowKey="transactionId"
            pagination={{ pageSize: 5 }}
            bordered
          />
        )}
      </Card>

      <Modal
        title={formData.transactionId ? "Edit Transaction" : "Add Transaction"}
        visible={isModalVisible}
        onCancel={closeModal}
        footer={null}
        destroyOnClose
      >
        <TransactionForm
          formData={formData}
          setFormData={setFormData}
          closeModal={closeModal}
          refetchData={fetchTransactions}
        />
      </Modal>
    </div>
  );
};

export default TransactionList;
